import {Injectable} from '@angular/core';
import {DataService} from './data.service';
import {NotificationService} from './notification.service';
import {take} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class EmailService {

  constructor(
    public tableDataService: DataService,
    private notification: NotificationService
  ) {
  }

  // Legacy JSON-based email send
  sendEmail(mailObj: any = null, silent = false) {
    // console.log('EmailService: Sending email with JSON payload', mailObj);
    this.tableDataService.post('email', mailObj).pipe(take(1))
      .subscribe({
        next: (result: any) => {
          if (result.Message !== 'Success' && result.Message !== 'Preflight') {
            this.notification.warning(result.description);
          } else if (result.Message === 'Success' && !silent) {
            // optional success toast handled by callers
          }
        },
        error: (err: any) => {
          console.error(err.message);
        },
        complete: () => {
        }
      });
  }

  // New: Send with attachments via FormData
  /**
   * Sends an email with optional attachments and various parameters for customization.
   *
   * @param {Object} payload - The email details and optional configuration.
   * @param {string} payload.toEmail - The recipient's email address.
   * @param {string} [payload.fromEmail] - The sender's email address.
   * @param {string} [payload.fromName] - The sender's name.
   * @param {string} payload.subject - The subject line of the email.
   * @param {string} payload.body - The main content of the email.
   * @param {any} [payload.header_id] - An optional identifier for the email header.
   * @param {any} [payload.entity_id] - An optional identifier related to the email context.
   * @param {string} [payload.activity_message] - A custom activity message associated with the email.
   * @param {boolean} [payload.includeAttachment] - Whether to include attachments in the email.
   * @param {string} [payload.pdfHtml] - The HTML content to generate a PDF attachment.
   * @param {string} [payload.fileName] - The desired name for the PDF attachment.
   * @param {string} [payload.FileName] - Alternate key for specifying the PDF file name.
   * @param {boolean} [payload.isBodyHtml] - Indicates if the email body is in HTML format.
   * @param {boolean} [payload.IsBodyHtml] - Alternate key for specifying if the email body is HTML.
   * @param {string} [payload.printOrientation] - The orientation of the printed content (e.g., portrait = 1 or landscape = 2).
   * @param {string} [payload.convertAttachmentTo] - The desired format for converting attachments (e.g., pdf or html).
   * @param {File[]} files - A list of files to be attached to the email.
   * @param {boolean} silent - If true, suppresses user notifications for success or errors.
   * @return {void}
   */
  sendEmailWithAttachments(
    payload: {
      toEmail: string;
      fromEmail?: string;
      fromName?: string;
      subject: string;
      body: string;
      header_id?: any;
      entity_id?: any;
      activity_message?: string;
      includeAttachment?: boolean;
      pdfHtml?: string;
      fileName?: string;
      FileName?: string;
      isBodyHtml?: boolean;
      IsBodyHtml?: boolean;
      printOrientation?: string;
      convertAttachmentTo?: string;
    },
    files: File[] = [],
    silent = false
  ) {
    const form = new FormData();

    // Choose one canonical key per field (aligns with your API signature)
    const toEmail = (payload.toEmail || '').trim();
    if (toEmail) {
      form.append('ToEmail', toEmail);
    }

    if (payload.fromEmail) {
      form.append('FromEmail', payload.fromEmail);
    }
    if (payload.fromName) {
      form.append('FromName', payload.fromName);
    }

    const subject = payload.subject ?? '';
    form.append('Subject', subject);

    const body = payload.body ?? '';
    form.append('Body', body);

    const printOreientation = payload.printOrientation ?? '';
    form.append('PrintOrientation', printOreientation);

    const convertType = payload.convertAttachmentTo ?? '';
    form.append('ReportFormatType', convertType);

    if (payload.header_id != null) {
      form.append('header_id', String(payload.header_id));
    }
    if (payload.entity_id != null) {
      form.append('entity_id', String(payload.entity_id));
    }
    if (payload.activity_message) {
      form.append('activity_message', payload.activity_message);
    }

    // Booleans as strings for FormData
    const includeAttachment = !!payload.includeAttachment;
    form.append('includeAttachment', String(includeAttachment));

    if (payload.pdfHtml) {
      form.append('PdfHtml', payload.pdfHtml);
    }

    const fileName = payload.FileName ?? payload.fileName;
    if (fileName) {
      form.append('FileName', fileName);
    }

    const isBodyHtml = payload.IsBodyHtml ?? payload.isBodyHtml;
    if (isBodyHtml != null) {
      form.append('IsBodyHtml', String(!!isBodyHtml));
    }

    // Files: append each file ONCE under a single field name
    (files || []).forEach((file) => {
      if (file) {
        form.append('attachments', file, file.name);
      }
    });

    // Optional: compact debug preview without duplicating entries
    try {
      const preview: any[] = [];
      (form as any).forEach?.((v: any, k: string) => {
        preview.push([k, v instanceof File ? `[file:${(v as File).name}]` : String(v)]);
      });
      // console.log('EmailService FormData preview:', preview, {selectedFileCount: files?.length || 0});
    } catch {
    }

    this.tableDataService.post('email/upload', form).pipe(take(1))
      .subscribe({
        next: (result: any) => {
          if (result?.Message && result.Message !== 'Success' && result.Message !== 'Preflight') {
            this.notification.warning(result.description || 'Email send failed');
          } else if (!silent) {
            // success toast handled by callers if needed
          }
        },
        error: (err: any) => {
          console.error(err?.message || err);
          this.notification.warning('Email send failed');
        },
        complete: () => {
        }
      });
  }
}
